import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import { GlobalContext } from "../context/global.context";
import { version } from "../config";
import { routeMap } from "../route";
import { useParams } from "react-router-dom";
import utils from "../utils/utils";
import useRecombee from "../hooks/useRecombee";
import recombee from "recombee-js-api-client";
import getFormattedData, { extractParameterValue } from "../utils/recombeeHandler";
import { hydratePageComponent } from "../utils/contentfulHandler";

/** Custom hook for to handle recommendation rail */
const useRecommendation = ({
  sysId: sysIdFromProp,
  query,
  pageType,
  pageName,
  rComp,
  catalogThemeRef,
  catalogComponentThemeDataRef,
  isRelatedRail,
  isPlayerRecommendationRail,
  playerRelatedComponent,
  mediaId,
  detailsSectionData,
  relatedRailContent,
  recommbeeAvailable,
  seriesId,
  type='',
  uid
}) => {
  const [railInfo, updateRailInfo] = useState({});
  const [railItemsInfo, updateRailItemsInfo] = useState(getPlaceholders());
  const [isNoData, setIsNoData] = useState(false);
  const { userInfo = {}, isAuthenticated, appLanguage, refreshCallState } = useContext(GlobalContext);
  const isScrollPaginationRef = useRef(false);
  const { pageComponent = {} } = useSelector((store) => store.navigation);
  const { appConfig } = useSelector((store) => store.appConfigReducers);
  const { pageComponents = {} } = pageComponent;
  const paramsX = useParams();
  const contentType = detailsSectionData?.type || type;
const contentId= contentType === 'movie' ? (uid || mediaId || seriesId) : (mediaId || seriesId || uid);
  if (isRelatedRail && recommbeeAvailable) {
    var {
      config: railConfig,
      componentStyles: rStyle,
      componentTheme: rTheme,
    } = hydratePageComponent(
      rComp,
      { contentType: paramsX.type },
      {
        defaultPageTheme: catalogThemeRef.current,
        defaultComponentTheme: catalogComponentThemeDataRef.current,
      }
    );
    var rConfig = { ...relatedRailContent, ...railConfig };
  }
  if (isPlayerRecommendationRail) {
    var { componentStyles: prStyle, componentTheme: prTheme } = hydratePageComponent(
      playerRelatedComponent,
      { contentType: playerRelatedComponent?.typeX },
      {
        defaultPageTheme: catalogThemeRef.current,
        defaultComponentTheme: catalogComponentThemeDataRef.current,
      }
    );
  }

  const sysId = playerRelatedComponent?.sysId || rConfig?.sysId || sysIdFromProp;
  const pComponent = pageComponents[sysId];
  const { config: configForRail = {}, componentStyle: componentStyleForRail = [], theme: themeForRail = {} } = pComponent || {};
  const [railDetails, setRailDetails] = useState({});

  const settingConfigForRail = () => {
    if (isRelatedRail && recommbeeAvailable) {
      setRailDetails({ config: rConfig, componentStyles: rStyle, componentTheme: rTheme });
    } else if (isPlayerRecommendationRail) {
      setRailDetails({ config: playerRelatedComponent, componentStyles: prStyle, componentTheme: prTheme });
    } else {
      setRailDetails({ config: configForRail, componentStyles: componentStyleForRail, componentTheme: themeForRail });
    }
  };
  useEffect(settingConfigForRail, [sysId, contentId, recommbeeAvailable]);

  const params = railDetails?.config?.params;
  const identifierExtfromConfig = railDetails?.config?.identifierExt;

  const region = extractParameterValue("region", params);
  const databaseId = extractParameterValue("databaseId", params);
  const pageNameRef = useRef("");
  const traceRef = useRef(null);
  const recombeeClient = useRecombee({ databaseId, region });
  const { userId: userIdFromUserInfo } = userInfo || {};
  const recombeeClientRef = useRef(recombeeClient);
  const userId = userInfo?.profile?.recommendationUserId || userIdFromUserInfo;

  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    recombeeClientRef.current = recombeeClient;
  }, [recombeeClient]);

  /** Fetches recommendation data */
  const fetchRecommendation = useCallback(
    (fnParams) => {
      let { railItems = [], config = {} } = fnParams || {};
      const { apiPageSize, maxItems, params } = config || {};
      const mediaTypeMap = {
        movie: '"movie"',
        series: '"series"',
        preview: '"preview"',
        episode: '"series"',
      };
      let reframedParams = params.replace("{user_id}", userId);
      reframedParams = reframedParams.replace("{media_type}", mediaTypeMap[contentType]);
      reframedParams = reframedParams.replace("{item_id}", contentId);
      const filter = extractParameterValue("filters", reframedParams);
      const recommendationFilter = extractParameterValue("recommendation_filter", reframedParams);
      const scenario = extractParameterValue("scenario", reframedParams);

      const selectedParams = {
        returnProperties: true, // Return properties of the recommended items
      };
      if (scenario) selectedParams.scenario = scenario;
      if (filter) {
        selectedParams.filter = filter;
      }
      setIsLoading(true);
      console.log("🚀 ~ userId on triggering:", userId);
      /**
       * Decide wether its ItemsToUser OR ItemsToItem
       */
      let recombeeAPI = new recombee.RecommendItemsToUser(userId, maxItems || apiPageSize || 20, selectedParams);
      if (recommendationFilter === "ItemsToItem") recombeeAPI = new recombee.RecommendItemsToItem(contentId, userId, maxItems || apiPageSize || 20, selectedParams);
      return recombeeClientRef.current
        ? recombeeClientRef.current
            .send(recombeeAPI)
            .then(function (res) {
              const content = getFormattedData(res, userId);
              railItems = [...railItems, ...content?.contentList];
              setIsLoading(false);
              return JSON.parse(JSON.stringify(railItems)); // For DC-14079
            })
            .catch(function (error) {
              setIsLoading(false);
              return [];
            })
        : Promise.resolve([]);
    },
    [userId, contentType, contentId]
  );

  useEffect(() => {
    pageNameRef.current = pageName;
  }, [pageName]);

  useEffect(() => {
    /**
     * Process rail configuration.
     */
    // if (refreshCallState !== "ENDED") return;
    if (!appConfig.baseUrl) return;
    if (!railDetails?.config?.sysId) return;

    const _query = utils.parse(query);
    const { config = {}, componentStyles = [], componentTheme = {} } = railDetails || {};
    const { identifierExt } = config;
    const maxItems = _query.viewall ? null : (config || {}).maxItems;
    if (identifierExt !== "related") return;
    const firebasePerformance = firebase.performance();
    let trace = null;
    if (!utils.isLocalHost()) {
      if (pageType === "listing") {
        trace = firebasePerformance.trace("listing_page_load");
        trace.putAttribute("pageTitle", "seeAll");
        if (typeof _query === "object" && Object.keys(_query).length > 0) {
          // let attr = {
          //   page: "Home",
          //   entry_point: identifierExt,
          // };
          //   mpLog({ type: "_content.browse", eventName: "select_page", attr: attr });
        }
      } else {
        const pageName = pageNameRef.current || "";
        trace = firebasePerformance.trace(pageName + "_carousel_load");
      }
      trace.putAttribute("appVersion", version.appVersion);
      trace.putAttribute("carouselId", identifierExtfromConfig || "recommendation");
      trace.start();
      traceRef.current = trace;
    }

    let style = componentStyles.map((style) => {
      return { ...style };
    });
    if (style.length > 0 && pageType === "listing") {
      style = style.map((style) => {
        style.type = "grid";
        if (!_query.viewall) {
          style.showComponentTitle = true;
          style.showItemTitle = true;
          style.itemSize = "medium";
          style.itemEdgeRadius = 0.25;
          style.itemOrientation = 0.667;
        }
        if (style.itemOrientation) {
          if (style.itemOrientation > 1) {
            style.maxItemTitleLines = 2; // landscape
          } else {
            style.maxItemTitleLines = 3; // portrait
          }
        }
        return { ...style };
      });
    }
    isScrollPaginationRef.current = isScrollPaginate(style);
    const viewAllLink = `${routeMap.listing}?sysId=${sysId}&viewall=true`;
    updateRailInfo({
      ...config,
      maxItems,
      style,
      viewAllLink,
      sysId,
      isRailItemPlayBtn: false,
      componentTheme: componentTheme,
      favouritesPage: false,
    });
    updateRailItemsInfo(getPlaceholders());
  }, [appConfig.baseUrl, railDetails, isAuthenticated, pComponent, pageType, query, sysId, refreshCallState, identifierExtfromConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  function initRecombeeFetch() {
    if (!appConfig.baseUrl) return;
    if (!railDetails?.config?.sysId) return;
    if (identifierExtfromConfig !== "related") return;
    if (!params) return;
    if (!isAuthenticated) return;
    if (refreshCallState !== "ENDED") return;
    if (!recombeeClient) return;
    if (!userId) return;

    const _query = utils.parse(query);
    const { config = {} } = railDetails || {};
    const maxItems = _query.viewall ? null : (config || {}).maxItems;

    const recommendationFilter = extractParameterValue("recommendation_filter", config?.params);
    if (recommendationFilter === "ItemsToItem" && !contentType) return;

    fetchRecommendation({
      currentPageNumber: 1,
      railItems: [],
      maxItems,
      config,
      baseUrl: appConfig.baseUrl,
      appLanguage,
    })
      .then((railItems = []) => {
        if (!railItems.length) {
          updateRailItemsInfo([]);
          setIsNoData(true);
        } else {
          updateRailItemsInfo(railItems);
          setIsNoData(false);
        }
      })
      .catch((error) => {
        updateRailItemsInfo([]);
        setIsNoData(true);
      });
  }
  useEffect(initRecombeeFetch, [contentType, isAuthenticated, sysId, refreshCallState, contentId, railDetails, recombeeClient, pageName, userId]);

  /** Return from Hook, including the recommended data. */
  return { railInfo, railItemsInfo, isNoData, isLoading };
};

const getPlaceholders = () => {
  /**
   * @return Rail placeholders data.
   */
  const items = [];
  for (let i = 0; i < 3; i++) items.push({ isPlaceholder: true });
  return items;
};

const isScrollPaginate = (style) => {
  /**
   * @return type of scroll.
   */
  return !(utils.isNotNull(style, 0, "type") && style[0].type === "grid");
};

export default useRecommendation;
